// src/services/authManager.js
// Wrapper que decide entre auth real o demoAuth

import { auth } from './auth';
import { demoAuth } from './demoAuth';
import { markWelcomePending } from '../utils/welcomeTour';

class AuthManager {
  constructor() {
    this.activeAuth = null;
    this.detectActiveAuth();
  }

  // Detectar qué sistema de auth está activo
  detectActiveAuth() {
    // Prioridad 1: Auth real (si hay token JWT)
    const authToken = localStorage.getItem('auth_token');
    if (authToken) {
      this.activeAuth = 'real';
      return;
    }

    // Prioridad 2: Demo auth (si hay current_user_id)
    const demoUserId = localStorage.getItem('current_user_id');
    if (demoUserId) {
      this.activeAuth = 'demo';
      return;
    }

    // Ninguno activo
    this.activeAuth = null;
  }

  // Obtener el servicio activo
  getActiveService() {
    this.detectActiveAuth();
    
    if (this.activeAuth === 'real') {
      return auth;
    } else if (this.activeAuth === 'demo') {
      return demoAuth;
    }
    
    return null;
  }

  // Login (detecta automáticamente)
  async login(email, password) {
    // Si es un email real, usar auth real
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    let result;
    if (emailRegex.test(email)) {
      this.activeAuth = 'real';
      result = await auth.login({ email, password });
    } else {
      this.activeAuth = 'demo';
      result = await demoAuth.login(email, password);
    }
    markWelcomePending(result?.user?.id);
    return result;
  }

  // Register
  async register({ email, password, name, inviteCode }) {
    this.activeAuth = 'real';
    return await auth.register({ email, password, name, inviteCode });
  }

  getTrialStatus() { return auth.getTrialStatus(); }

  activateTrial(password) { return auth.activateTrial(password); }

  // Crear sesión demo
  async createDemoSession(name) {
    this.activeAuth = 'real'; // Demo usa el auth real ahora
    return await auth.createDemoSession(name);
  }

  // Logout
  logout() {
    const service = this.getActiveService();
    if (service) {
      service.logout();
    }
    this.activeAuth = null;
  }

  // Get current user
  getCurrentUser() {
    const service = this.getActiveService();
    return service ? service.getCurrentUser() : null;
  }

  // Is authenticated
  isAuthenticated() {
    return this.getCurrentUser() !== null;
  }

  // Is demo
  isDemoMode() {
    if (this.activeAuth === 'demo') return true;
    if (this.activeAuth === 'real') return auth.isDemoMode();
    return false;
  }

  // Needs email verification
  needsEmailVerification() {
    if (this.activeAuth === 'real') {
      return auth.needsEmailVerification();
    }
    return false;
  }

  // Proxy methods para auth real
  verifyEmail(token) {
    return auth.verifyEmail(token);
  }

  forgotPassword(email) {
    return auth.forgotPassword(email);
  }

  resetPassword(data) {
    return auth.resetPassword(data);
  }

  refreshToken() {
    if (this.activeAuth === 'real') {
      return auth.refreshToken();
    }
  }

  // Proxy methods para demo (planes, invitaciones, etc.)
  purchasePlan(planId, userInfo) {
    if (this.activeAuth === 'demo') {
      return demoAuth.purchasePlan(planId, userInfo);
    }
    // TODO: Implementar compra real con Stripe
    throw new Error('Compra de planes solo disponible en demo por ahora');
  }

  getCurrentAccount() {
    if (this.activeAuth === 'demo') {
      return demoAuth.getCurrentAccount();
    }
    // TODO: Implementar cuentas reales
    return null;
  }

  getCurrentSubscription() {
    if (this.activeAuth === 'demo') {
      return demoAuth.getCurrentSubscription();
    }
    // TODO: Implementar subscripciones reales
    return null;
  }

  inviteUser(email) {
    if (this.activeAuth === 'demo') {
      return demoAuth.inviteUser(email);
    }
    // TODO: Implementar invitaciones reales
    throw new Error('Invitaciones solo disponibles en demo por ahora');
  }

  getAccountMembers() {
    if (this.activeAuth === 'demo') {
      return demoAuth.getAccountMembers();
    }
    return [];
  }

  getAccountInvitations() {
    if (this.activeAuth === 'demo') {
      return demoAuth.getAccountInvitations();
    }
    return [];
  }

  async saveProfile(profileData) {
    if (this.activeAuth === 'demo') {
      return demoAuth.saveProfile(profileData);
    }
    // Usar API real
    return auth.saveProfile(profileData);
  }

  async getProfile(options) {
    if (this.activeAuth === 'demo') {
      return demoAuth.getCurrentProfile();
    }
    // Obtener perfil real desde API
    return auth.getProfile(options);
  }

  getCurrentProfile() {
    // Método sincrónico para compatibilidad
    if (this.activeAuth === 'demo') {
      return demoAuth.getCurrentProfile();
    }
    // Para auth real, se debe usar getProfile() async
    return null;
  }
}

// Instancia singleton
export const authManager = new AuthManager();

// Funciones de utilidad
export const isAuthenticated = () => authManager.isAuthenticated();
export const getCurrentUser = () => authManager.getCurrentUser();
export const isDemoMode = () => authManager.isDemoMode();
export const PLANS = demoAuth.constructor.PLANS || {};
